import { useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import i18n from 'i18n'
import { useLocation } from 'react-router-dom'

import type {
    AccountActivateRequest,
    AuthenticatePinRequest,
    AuthenticationRequest,
    CancelVendorCustomRequest,
    ChangeApiUrlRequest,
    ChangeEmailRequest,
    ChangePasswordRequest,
    ChangeSecretWordRequest,
    CountryCodebookDTO,
    DisputeRequest,
    ExistingUserVendorCustomRequest,
    IdentityCardValidationRequest,
    NewPasswordRequest,
    PasswordResetRequest,
    PaymentPlanRequestDTO,
    PersonVerifyRequest,
    SecurityQuestionChangeRequest,
    SendPreQualifyNotificationRequest,
    TransactionDeliveryRequest,
    TransactionPartialDeliveryRequest,
    TransactionRequestDTO,
    VendorAgentRequestDTO,
    VendorContactChangeRequest,
    VendorCustomRequest,
    VendorPaymentInvoiceSearchRequest,
    VendorRemoteOfferRequest,
    VendorUserPasswordChangeRequestDTO,
    VendorUserRequestDTO,
} from '@leanpay/models'
import { logoutUser, setAuthToken, timeService, vendorService } from '@leanpay/services'

import { queryClient } from 'app/app'
import { useAppStore } from 'app/app-context'
import { CacheKey, countryCodebookId, DEFAULT_FROM_DATE, DEFAULT_TO_DATE } from 'library/constants'

export const useGetSessionInfo = () => {
    const query = useQuery({
        queryKey: [CacheKey.SessionInfo],
        queryFn: () => vendorService.getSessionInfo(),
        refetchOnWindowFocus: false,
        retry: (count, error: any) => {
            if (new Error(error).message === '401') {
                logoutUser()
                return false
            }
            return true
        },
    })

    useEffect(() => {
        if (query.isSuccess) {
            void queryClient.invalidateQueries({ queryKey: [CacheKey.CurrencyInfo] })
        }
    }, [query.isSuccess])

    return query
}

export const useAuthenticate = () => {
    const { setIsAuthenticated } = useAppStore()
    const mutation = useMutation({
        mutationFn: (data: AuthenticationRequest) => {
            return vendorService.authenticate(data)
        },
        retry: false,
        onSuccess: (data) => {
            void queryClient.fetchQuery({ queryKey: [CacheKey.SessionInfo] })
            setAuthToken(data.idToken)
            setIsAuthenticated(true)
        },
    })

    return mutation
}

export const useGetVendorRequestInfo = (isInStore: boolean) => {
    const location = useLocation()
    return useQuery({
        queryKey: [CacheKey.VendorRequestInfo, isInStore],
        queryFn: () => vendorService.getVendorRequestInfo(isInStore),
        refetchOnWindowFocus: false,
        enabled: location.pathname !== '/login',
        retry: (count, error: any) => {
            if (new Error(error).message === '401') {
                logoutUser()
                return false
            }
            return true
        },
    })
}

export const useGetCustomOffers = (filters: Record<string, string | undefined>) => {
    const pageIndex = filters.pageIndex ?? 0
    const pageSize = filters.pageSize ?? 10
    const searchCriteria = filters.searchCriteria ?? ''

    const createdFrom = filters.createdFrom
        ? format(new Date(filters.createdFrom), 'yyyy-MM-dd')
        : format(new Date(DEFAULT_FROM_DATE), 'yyyy-MM-dd')
    const createdTo = filters.createdTo
        ? format(new Date(filters.createdTo), 'yyyy-MM-dd')
        : format(new Date(DEFAULT_TO_DATE), 'yyyy-MM-dd')

    return useQuery({
        queryKey: [CacheKey.CustomOffers, filters],
        queryFn: () =>
            vendorService.getCustomOffers({
                offersSize: { page: +pageIndex, size: +pageSize },
                searchCriteria,
                createdFrom,
                createdTo,
            }),
        refetchOnWindowFocus: false,
    })
}

export const useGetVendorUsers = (filters: Record<string, string | undefined>) => {
    const pageIndex = filters.pageIndex ?? 0
    const pageSize = filters.pageSize ?? 10
    const searchQuery = filters.searchCriteria ?? ''
    return useQuery({
        queryKey: [CacheKey.VendorUsers, filters],
        queryFn: () =>
            vendorService.searchVendorUsers({
                searchCriteria: searchQuery,
                page: +pageIndex,
                size: +pageSize,
            }),
        refetchOnWindowFocus: false,
    })
}
export const useGetVerificationAgents = (filters: Record<string, string | undefined>) => {
    const pageIndex = filters.pageIndex ?? 0
    const pageSize = filters.pageSize ?? 10
    const searchQuery = filters.searchCriteria ?? ''
    return useQuery({
        queryKey: [CacheKey.VerificationAgents, filters],
        queryFn: () =>
            vendorService.searchVerificationAgents({
                searchCriteria: searchQuery,
                page: +pageIndex,
                size: +pageSize,
            }),
        refetchOnWindowFocus: false,
    })
}

export const useGetTransactions = (
    request: TransactionRequestDTO,
    filters: Record<string, string | undefined>,
) => {
    const pageIndex = filters.pageIndex ?? 0
    const pageSize = filters.pageSize ?? 10
    return useQuery({
        queryKey: [CacheKey.Transactions, filters],
        queryFn: () =>
            vendorService.getTransactions({ request, page: +pageIndex, size: +pageSize }),
        refetchOnWindowFocus: false,
    })
}

export const useGetCurrency = () => {
    return useQuery({
        queryKey: [CacheKey.CurrencyInfo],
        queryFn: () => vendorService.getCurrencyFromServer(),
        staleTime: Infinity,
    })
}

export const useGetPostZipsByCountry = () => {
    return useQuery({
        queryKey: [CacheKey.PostZip],
        enabled: !!countryCodebookId,
        queryFn: () => vendorService.getPostZips(countryCodebookId),
    })
}

export const useGetCountries = () => {
    return useQuery({
        queryKey: [CacheKey.Country],
        queryFn: () => vendorService.getCountryCodebook(),
        // Static codebook — never refetch within a session
        staleTime: Infinity,
        // Market country (Romania) listed first, rest in server order
        select: (countries: CountryCodebookDTO[]) => {
            const marketCountry = countries.find((c) => c.id.toString() === countryCodebookId)
            return marketCountry
                ? [marketCountry, ...countries.filter((c) => c.id.toString() !== countryCodebookId)]
                : countries
        },
    })
}

export const useVerifyEmail = () => {
    const mutation = useMutation({
        mutationFn: (data: AccountActivateRequest) => vendorService.verifyEmail(data),
    })

    return mutation
}

export const useGetStreetsByPostZip = (zipCode: string) => {
    return useQuery({
        queryKey: [CacheKey.Streets, zipCode],
        enabled: !!zipCode,
        queryFn: () => vendorService.getStreetsByPostZip(zipCode),
    })
}

export const useGetSettlementsByStreet = (streetId: string) => {
    return useQuery({
        queryKey: [CacheKey.Settlement, streetId],
        enabled: !!streetId,
        refetchOnWindowFocus: true,
        queryFn: () => vendorService.getSettlementsByStreet(streetId),
    })
}

export const useGetSettlementsByPostZip = (postZipCodebookId: string) => {
    return useQuery({
        queryKey: [CacheKey.SettlementByPostZip, postZipCodebookId],
        enabled: !!postZipCodebookId,
        queryFn: () => vendorService.getSettlementsByPostZip(postZipCodebookId),
    })
}

export const useGetStreetsBySettlement = (settlementCodebookId: string) => {
    return useQuery({
        queryKey: [CacheKey.StreetsBySettlement, settlementCodebookId],
        enabled: !!settlementCodebookId,
        queryFn: () => vendorService.getStreetsBySettlement(settlementCodebookId),
    })
}

export const useGetTransaction = (id: number) => {
    return useQuery({
        queryKey: [CacheKey.Transaction, id],
        queryFn: () => vendorService.getTransaction(id),
    })
}
export const useGetVendorUserRoles = () => {
    return useQuery({
        queryKey: [CacheKey.VendorUserRole],
        queryFn: () => vendorService.getVendorUserRoles(),
    })
}
export const useGetVendorData = (lng: string) => {
    return useQuery({
        queryKey: [CacheKey.VendorData, lng],
        queryFn: () => vendorService.getVendorData(lng),
    })
}

export const useGetCustomOffer = (id: number) => {
    return useQuery({
        queryKey: [CacheKey.CustomOffer, id],
        queryFn: () => vendorService.getCustomOffer(id),
    })
}
export const useGetVendorUser = (id: number) => {
    return useQuery({
        queryKey: [CacheKey.VendorUser, id],
        queryFn: () => vendorService.getVendorUser(id),
    })
}
export const useGetVerificationAgent = (id: number) => {
    return useQuery({
        queryKey: [CacheKey.VerificationAgent, id],
        queryFn: () => vendorService.getVerificationAgent(id),
    })
}

export const useCreateOffer = () => {
    const mutation = useMutation({
        mutationFn: (data: VendorCustomRequest) => vendorService.createOffer(data),
    })

    return mutation
}
export const useCreateRemoteOffer = () => {
    const mutation = useMutation({
        mutationFn: (data: VendorRemoteOfferRequest) => vendorService.createRemoteOffer(data),
    })

    return mutation
}
export const useCancelOffer = () => {
    const mutation = useMutation({
        mutationFn: (data: CancelVendorCustomRequest) => vendorService.cancelOffer(data),
    })

    return mutation
}
export const useChangeVendorPassword = () => {
    const mutation = useMutation({
        mutationFn: (data: VendorUserPasswordChangeRequestDTO) =>
            vendorService.changePasswordForVendorUser(data),
    })

    return mutation
}
export const useResetVendorPassword = () => {
    const mutation = useMutation({
        mutationFn: (data: PasswordResetRequest) => vendorService.resetPasswordForVendorUser(data),
    })

    return mutation
}
export const useDisputeTransaction = () => {
    const mutation = useMutation({
        mutationFn: (data: DisputeRequest) => vendorService.disputeTransaction(data),
    })

    return mutation
}
export const useAddVendorUser = () => {
    const mutation = useMutation({
        mutationFn: (data: VendorUserRequestDTO) => vendorService.addVendorUser(data),
    })
    return mutation
}
export const useAddVerificationAgent = () => {
    const mutation = useMutation({
        mutationFn: (data: VendorAgentRequestDTO) => vendorService.addVerificationAgent(data),
    })
    return mutation
}
export const useEditVendorUser = (id: number) => {
    const mutation = useMutation({
        mutationFn: (data: VendorUserRequestDTO) => vendorService.editVendorUser(data, id),
    })
    return mutation
}
export const useEditVerificationAgent = (id: number) => {
    const mutation = useMutation({
        mutationFn: (data: VendorAgentRequestDTO) =>
            vendorService.updateVerificationAgent(data, id),
    })
    return mutation
}

export const useDeliveryTransaction = () => {
    const mutation = useMutation({
        mutationFn: (data: TransactionDeliveryRequest) => vendorService.doTransactionDelivery(data),
    })

    return mutation
}

export const usePartialDeliveryTransaction = () => {
    const mutation = useMutation({
        mutationFn: (data: TransactionPartialDeliveryRequest) =>
            vendorService.doTransactionPartialDelivery(data),
    })

    return mutation
}
export const useCreateOfferForVerifiedUser = () => {
    const mutation = useMutation({
        mutationFn: (data: ExistingUserVendorCustomRequest) =>
            vendorService.createOfferForVerifiedUser(data),
    })

    return mutation
}

export const useValidateIdentityCard = () => {
    const mutation = useMutation({
        mutationFn: (data: IdentityCardValidationRequest) =>
            vendorService.validateIdentityCard(data),
    })

    return mutation
}
export const useUpdateContactInfo = () => {
    const mutation = useMutation({
        mutationFn: (data: VendorContactChangeRequest) => vendorService.updateContactInfo(data),
    })

    return mutation
}

export const useGetPaymentPlans = (request: PaymentPlanRequestDTO) => {
    return useQuery({
        queryKey: [CacheKey.PaymentPlans, request.vendorProductCode, request.amount],
        enabled: !!request.vendorProductCode && !!request.amount,
        queryFn: () => vendorService.calculatePaymentPlans(request),
    })
}
export const useVerifyPerson = () => {
    const mutation = useMutation({
        mutationFn: (data: PersonVerifyRequest) => vendorService.verifyPerson(data),
    })

    return mutation
}
// RO in-store OTP consent (TEC-19045)
export const useRequestPin = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) => vendorService.requestPin(phoneNumber),
    })

    return mutation
}
export const useAuthenticatePin = () => {
    const mutation = useMutation({
        mutationFn: (data: AuthenticatePinRequest) => vendorService.authenticatePin(data),
    })

    return mutation
}
export const useGetCustomerBaseInfo = (phoneNumber: string) => {
    return useQuery({
        queryKey: [CacheKey.CustomerBaseInfo, phoneNumber],
        enabled: !!phoneNumber,
        queryFn: () => vendorService.getCustomerBaseInfo(phoneNumber),
    })
}

export const useSearchInvoices = (
    request: VendorPaymentInvoiceSearchRequest,
    filters: Record<string, string | undefined>,
) => {
    const pageIndex = filters.pageIndex ?? 0
    const pageSize = filters.pageSize ?? 10
    return useQuery({
        queryKey: [CacheKey.Invoices, filters],
        queryFn: () =>
            vendorService.searchInvoices({ payload: request, page: +pageIndex, size: +pageSize }),
        refetchOnWindowFocus: false,
    })
}
export const useGetPaymentInvoice = (id: number) => {
    return useQuery({
        queryKey: [CacheKey.Invoice, id],
        queryFn: () => vendorService.getPaymentInvoice(id),
        refetchOnWindowFocus: false,
    })
}

export const useGetAdministrativeUnits = () => {
    return useQuery({
        queryKey: [CacheKey.AdministrativeUnits],
        queryFn: () => vendorService.getAdministrativeUnits(countryCodebookId),
    })
}

export const useGetCurrentTime = () => {
    return useQuery({
        queryKey: [CacheKey.Time],
        queryFn: async () => timeService.getCurrentTime(),
    })
}
export const useGetSecurityQuestions = () => {
    const lng = i18n.language
    return useQuery({
        queryKey: [CacheKey.SecurityQuestions],
        queryFn: async () => vendorService.getSecurityQuestions(lng),
    })
}

export const useChangeSecurityEmail = () => {
    const mutation = useMutation({
        mutationFn: (data: ChangeEmailRequest) => vendorService.changeEmail(data),
    })

    return mutation
}
export const useChangeSecurityQuestion = () => {
    const mutation = useMutation({
        mutationFn: (data: SecurityQuestionChangeRequest) =>
            vendorService.changeSecurityQuestion(data),
    })

    return mutation
}
export const useChangeSecretWord = () => {
    const mutation = useMutation({
        mutationFn: (data: ChangeSecretWordRequest) => vendorService.changeSecretWord(data),
    })

    return mutation
}

export const useChangeApiUrl = () => {
    const mutation = useMutation({
        mutationFn: (data: ChangeApiUrlRequest) => vendorService.changeApiUrl(data),
    })

    return mutation
}

export const useChangeSecurityPassword = () => {
    const mutation = useMutation({
        mutationFn: (data: ChangePasswordRequest) => vendorService.changePassword(data),
    })

    return mutation
}

export const useResendVerifyEmail = () => {
    const mutation = useMutation({
        mutationFn: () => vendorService.resendVerifyEmail(),
    })

    return mutation
}

export const useGetPqQrCode = () => {
    return useQuery({
        queryKey: [CacheKey.PqQrCode],
        queryFn: () => vendorService.getQrCode(),
    })
}

export const useRequestPasswordReset = () => {
    const mutation = useMutation({
        mutationFn: (data: PasswordResetRequest) => vendorService.requestPasswordReset(data),
    })

    return mutation
}

export const usePasswordSet = () => {
    const mutation = useMutation({
        mutationFn: (data: NewPasswordRequest) => vendorService.passwordSet(data),
    })

    return mutation
}

export const useSendSMS = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) =>
            vendorService.sendSms({
                phoneNumber,
            }),
    })

    return mutation
}

export const useSendNotification = () => {
    const mutation = useMutation({
        mutationFn: (data: SendPreQualifyNotificationRequest) =>
            vendorService.sendNotification(data),
    })

    return mutation
}
